import React, { useState, useEffect, useRef } from 'react'
import { Box, Tabs, Tab, IconButton, TextField, Button } from '@mui/material';
import { ProductionTest } from './productionTest';
import { AccumulatedProduction } from './accumulatedProduct';
import TableRowsIcon from '@mui/icons-material/TableRows';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

function a11yProps(index: number) {
  return {
    id: `production-tab-${index}`,
    'aria-controls': `production-tabpanel-${index}`,
  };
}

function CustomTabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`production-tabpanel-${index}`}
      aria-labelledby={`production-tab-${index}`}
      style={{ 
        display: value === index ? 'flex' : 'none', 
        flexDirection: 'column', 
        flex: 1, 
        overflow: 'hidden',
        paddingTop: '12px'
      }}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

export const ProductionModule = () => {
  const [value, setValue] = useState(0);
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const resetDatesRef = useRef<(() => void) | null>(null);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    // Resetear fechas al cambiar de tab para que se inicialicen con los datos del nuevo tab
    setStartDate('');
    setEndDate('');
  };

  const handleResetDates = () => {
    if (resetDatesRef.current) {
      resetDatesRef.current();
    }
  };

  return (
    <Box className="table-container" sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          scrollButtons="auto"
          variant="fullWidth"
        >
          <Tab
            label="Pruebas de Producción"
            {...a11yProps(0)}
          />
          <Tab
            label="Producción Acumulada"
            {...a11yProps(1)}
          />
        </Tabs>
      </Box>

      {/* Icon Buttons y Filtros de fecha debajo de las tabs */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1,
          p: 1,
          borderBottom: 1,
          borderColor: "divider",
          alignItems: "center",
        }}
      >
        <IconButton
          onClick={() => setViewMode('table')}
          sx={{
            backgroundColor: viewMode === 'table' ? "#1976d2" : "#f0f0f0",
            color: viewMode === 'table' ? "#fff" : "#000",
            "&:hover": {
              backgroundColor: viewMode === 'table' ? "#42a5f5" : "#e0e0e0",
            },
          }}
        >
          <TableRowsIcon />
        </IconButton>
        <IconButton
          onClick={() => setViewMode('chart')}
          sx={{
            backgroundColor: viewMode === 'chart' ? "#1976d2" : "#f0f0f0",
            color: viewMode === 'chart' ? "#fff" : "#000",
            "&:hover": {
              backgroundColor: viewMode === 'chart' ? "#42a5f5" : "#e0e0e0",
            },
          }}
        >
          <AssessmentIcon />
        </IconButton>
        
        {/* Filtros de fecha - solo visibles en modo gráfica */}
        {viewMode === 'chart' && (
          <Box sx={{ display: "flex", gap: 2, ml: "auto", alignItems: "center" }}>
            <TextField
              label="Fecha Inicio"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Fecha Fin"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ minWidth: 200 }}
            />
            <IconButton
              onClick={handleResetDates}
              sx={{ minWidth: 20 }}
            >
              <RestartAltIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <CustomTabPanel value={value} index={0}>
          <ProductionTest 
            viewMode={viewMode} 
            setViewMode={setViewMode}
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            resetDatesRef={resetDatesRef}
          />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <AccumulatedProduction 
            viewMode={viewMode} 
            setViewMode={setViewMode}
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            resetDatesRef={resetDatesRef}
          />
        </CustomTabPanel>
      </Box>
    </Box>
  )
}